import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { CheckCircle, XCircle } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import { useConfig } from 'hooks/use-config';
import { refreshAccessToken } from 'features/auth/services/auth-service';
import klaxLogo from 'assets/images/Vector.svg';
import { useTranslation } from 'react-i18next';

export function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();
    const paymentDetailId = searchParams.get('paymentDetailId');
    const status = searchParams.get('status');
    const isFailed = status === 'failed';
    const { theme } = useTheme();
    const { configurationSettings } = useConfig();
    const { t } = useTranslation();

    // Refresh access token when payment is successful to get updated permissions
    useEffect(() => {
        const refreshTokenAfterPayment = async () => {
            if (configurationSettings && !isFailed) {
                try {
                    await refreshAccessToken(configurationSettings);
                } catch (error) {
                    console.warn('Failed to refresh access token after successful payment:', error);
                    // Don't block the user experience if token refresh fails
                }
            }
        };

        refreshTokenAfterPayment();
    }, [configurationSettings]);

    const handleButtonClick = () => {
        if (isFailed) {
            window.location.href = '/subscription-plans';
        } else {
            // Reload the window to ensure all stores are refreshed with updated user data
            window.location.href = '/chat';
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4 w-full">
            <div className="max-w-2xl w-full text-center space-y-8">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img
                        src={klaxLogo}
                        alt="KLAX!"
                        className={`h-auto w-64 object-contain ${theme === 'dark' ? '' : 'drop-shadow-[0_4px_8px_rgba(0,0,0,0.6)]'}`}
                    />
                </div>

                {/* Status Icon */}
                <div className="flex justify-center">
                    <div className="w-[334px] h-[334px] flex items-center justify-center">
                        {isFailed ? (
                            <XCircle className="w-48 h-48 text-destructive" strokeWidth={1} />
                        ) : (
                            <CheckCircle className="w-48 h-48 text-success-green" strokeWidth={1} />
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-8">
                    <div className="space-y-4">
                        {/* Title */}
                        <h1 className="text-[32px] font-bold leading-[48px] text-foreground font-sans">
                            {isFailed ? t('PAYMENT_FAILED', 'Payment failed') : t('PAYMENT_SUCCESSFUL')}
                        </h1>

                        {paymentDetailId && (
                            <div className="flex items-center justify-center gap-2">
                                <span className="font-semibold text-foreground">
                                    {t('PAYMENT_DETAIL_ID')}:
                                </span>
                                <span className="text-[20px] font-normal leading-[30px] text-muted-foreground font-sans">
                                    {paymentDetailId}
                                </span>
                            </div>
                        )}

                        {/* Description */}
                        {!isFailed && (
                            <p className="text-[20px] font-normal leading-[30px] text-muted-foreground font-sans">
                                {t('THANK_YOU_FOR_YOUR_SUBSCRIPTION')}
                            </p>
                        )}
                    </div>

                    {/* Button */}
                    <div className="flex justify-center">
                        <Button
                            onClick={handleButtonClick}
                            className="w-[330px] h-[46px] bg-primary hover:bg-primary-600 font-semibold text-[15px] leading-[26px] rounded-md px-4 py-2.5 transition-colors"
                        >
                            {isFailed ? t('TRY_AGAIN', 'Try Again') : t('START_USING_APP')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}